import {
  PERMISSION_ENTITY_EXTERNAL_BUFFER_FEE,
  PERMISSION_ENTITY_INVOICE,
  PERMISSION_ENTITY_INVOICE_OFFER,
  PERMISSION_ENTITY_OPERATIONAL,
  PERMISSION_ENTITY_PAAS_OFFER,
  PERMISSION_ENTITY_PAYMENT,
  PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE,
  PERMISSION_ENTITY_PURCHASE_ORDER,
  PERMISSION_ENTITY_LATE_FEE,
  PERMISSION_ENTITY_PLATFORM_FEE
} from '@klear/constants'
import { FEE_ACTION_LABELS, FEE_STATUS_LABELS } from '@klear/fee/presentation'
import { INVOICE_ACTION_LABELS, INVOICE_STATUS_LABELS } from '@klear/invoice/presentation'
import { INVOICE_OFFER_ACTION_LABELS, INVOICE_OFFER_STATUS_LABELS } from '@klear/invoice-offer/presentation'
import { OPERATIONAL_ACTION_LABELS, OPERATIONAL_STATUS_LABELS } from '@klear/operational/presentation'
import { PAAS_OFFER_ACTION_LABELS, PAAS_OFFER_STATUS_LABELS } from '@klear/paas/paas-offer-presentation'
import {
  PAYMENT_ACTION_LABELS,
  PAYMENT_LIFECYCLE_STATUS_LABELS,
  PAYMENT_STATUS_LABELS
} from '@klear/payment/presentation'
import {
  PROOF_OF_ACCEPTANCE_ACTION_LABELS,
  PROOF_OF_ACCEPTANCE_STATUS_LABELS
} from '@klear/proof-of-acceptance/presentation'
import { PURCHASE_ORDER_ACTION_LABELS, PURCHASE_ORDER_STATUS_LABELS } from '@klear/purchase-order/presentation'
import type {
  OrderEventActionBulkEnum,
  OrderEventActionEnum,
  OrderEventOrderEventType
} from '@klear/order-event/constants'
import type { OrderEventLifecycleStateFor, OrderEventStatusViewFor } from '@klear/order-event/status-view'
import values from 'lodash/values'

type Labels = Readonly<Partial<Record<string, string>>>

const STATUS_LABELS_BY_TYPE: Readonly<Record<string, Labels>> = {
  [PERMISSION_ENTITY_PURCHASE_ORDER]: PURCHASE_ORDER_STATUS_LABELS,
  [PERMISSION_ENTITY_INVOICE]: INVOICE_STATUS_LABELS,
  [PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE]: PROOF_OF_ACCEPTANCE_STATUS_LABELS,
  [PERMISSION_ENTITY_INVOICE_OFFER]: INVOICE_OFFER_STATUS_LABELS,
  [PERMISSION_ENTITY_PAAS_OFFER]: PAAS_OFFER_STATUS_LABELS,
  [PERMISSION_ENTITY_OPERATIONAL]: OPERATIONAL_STATUS_LABELS,
  [PERMISSION_ENTITY_PAYMENT]: { ...PAYMENT_LIFECYCLE_STATUS_LABELS, ...PAYMENT_STATUS_LABELS },
  [PERMISSION_ENTITY_PLATFORM_FEE]: FEE_STATUS_LABELS,
  [PERMISSION_ENTITY_EXTERNAL_BUFFER_FEE]: FEE_STATUS_LABELS,
  [PERMISSION_ENTITY_LATE_FEE]: FEE_STATUS_LABELS
} satisfies Record<OrderEventOrderEventType, Labels>

const ACTION_LABELS_BY_TYPE: Readonly<Record<string, Labels>> = {
  [PERMISSION_ENTITY_PURCHASE_ORDER]: PURCHASE_ORDER_ACTION_LABELS,
  [PERMISSION_ENTITY_INVOICE]: INVOICE_ACTION_LABELS,
  [PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE]: PROOF_OF_ACCEPTANCE_ACTION_LABELS,
  [PERMISSION_ENTITY_INVOICE_OFFER]: INVOICE_OFFER_ACTION_LABELS,
  [PERMISSION_ENTITY_PAAS_OFFER]: PAAS_OFFER_ACTION_LABELS,
  [PERMISSION_ENTITY_OPERATIONAL]: OPERATIONAL_ACTION_LABELS,
  [PERMISSION_ENTITY_PAYMENT]: PAYMENT_ACTION_LABELS,
  [PERMISSION_ENTITY_PLATFORM_FEE]: FEE_ACTION_LABELS,
  [PERMISSION_ENTITY_EXTERNAL_BUFFER_FEE]: FEE_ACTION_LABELS,
  [PERMISSION_ENTITY_LATE_FEE]: FEE_ACTION_LABELS
} satisfies Record<OrderEventOrderEventType, Labels>

type CanonicalStatusEventType = Exclude<OrderEventOrderEventType, typeof PERMISSION_ENTITY_PAYMENT>

export function orderEventStatusView<TType extends CanonicalStatusEventType>(
  eventType: TType,
  lifecycleState: OrderEventLifecycleStateFor<TType>
): OrderEventStatusViewFor<TType> {
  const label = STATUS_LABELS_BY_TYPE[eventType]?.[lifecycleState]
  if (label == null) throw new Error(`Missing ${eventType} status label for ${lifecycleState}`)
  return { lifecycleState, status: lifecycleState, statusLabel: label } as OrderEventStatusViewFor<TType>
}

export function orderEventShowsStatus(eventType: string): boolean {
  return eventType !== PERMISSION_ENTITY_OPERATIONAL
}

export function orderEventActionLabel(eventType: OrderEventOrderEventType, action: OrderEventActionEnum): string {
  const label = ACTION_LABELS_BY_TYPE[eventType]?.[action]
  if (label == null) throw new Error(`Missing ${eventType} label for order-event action ${action}`)
  return label
}

export function orderEventBulkActionLabel(action: OrderEventActionBulkEnum): string {
  const domainLabelSets: readonly Labels[] = values(ACTION_LABELS_BY_TYPE)
  const labels = new Set(
    domainLabelSets.map((domainLabels) => domainLabels[action]).filter((label): label is string => label != null)
  )
  if (labels.size !== 1) {
    throw new Error(`Order-event bulk action ${action} has ${labels.size === 0 ? 'no label' : 'conflicting labels'}`)
  }
  return [...labels][0]!
}
